import React, { Fragment } from "react";
import { Box } from "@mui/material";
import { SectionHeading, LabelText, ValueText } from "../Common/Typography/AppTypography";

const ParamRow = ({ label, value, unit = "" }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      mb: 1.25,
    }}
  >
    <Box sx={{ flex: "0 0 75%", maxWidth: "75%", fontSize: "0.8125rem", color: "text.primary" }}>
      {label}
    </Box>
    <Box sx={{ flex: "0 0 25%", maxWidth: "25%", fontSize: "0.8125rem", fontWeight: 500, color: "text.primary" }}>
      {value}
      {unit && (
        <ValueText component="span" sx={{ ml: 0.5, color: "#9e9e9e", fontSize: "0.8125rem" }}>
          {unit}
        </ValueText>
      )}
    </Box>
  </Box>
);

const SectionBlock = ({ children, isFirst, verticalLabel }) => (
  <Box
    sx={{
      position: "relative",
      pl: 6.25,
      py: 1.25,
      minHeight: 200,
      borderTop: isFirst ? "none" : "1px solid #dbdbdb",
      pt: isFirst ? 0 : 1.5,
    }}
  >
    {verticalLabel && (
      <Box
        sx={{
          position: "absolute",
          top: "5%",
          left: -110,
          color: "#cccccc",
          transform: "rotate(270deg)",
          transformOrigin: "90% 40%",
          minWidth: 150,
          fontSize: "0.8125rem",
        }}
      >
        {verticalLabel}
      </Box>
    )}
    {children}
  </Box>
);

const DiagnosticData = ({ diagnosticData, deviceID, modelNumber, showHeader = true }) => {
  if (!diagnosticData) return null;
  const d = diagnosticData;

  return (
    <Fragment>
      <Box component="article" aria-label="Diagnostics data">
        {showHeader && (
          <Box sx={{ textAlign: "center", py: 2.5 }}>
            <SectionHeading sx={{ mb: 0 }}>Diagnostics Data</SectionHeading>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            py: 1,
            px: 2,
            mb: 1.5,
            bgcolor: "#f3f3f3",
            minHeight: 32,
            borderRadius: 0,
          }}
        >
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <LabelText component="span" sx={{ mr: 1 }}>
              Device ID:
            </LabelText>
            <ValueText component="span" sx={{ fontWeight: 600 }}>
              {deviceID}
            </ValueText>
          </Box>
          <Box sx={{ display: "inline-flex", alignItems: "center" }}>
            <LabelText component="span" sx={{ mr: 1 }}>
              Model Number:
            </LabelText>
            <ValueText component="span" sx={{ fontWeight: 600 }}>
              {modelNumber}
            </ValueText>
          </Box>
        </Box>

        <Box sx={{ pb: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              pl: 6.25,
              py: 1,
              borderBottom: "1px solid #dbdbdb",
            }}
          >
            <Box sx={{ flex: "0 0 75%", fontSize: "0.875rem", fontWeight: 600, color: "primary.main" }}>
              Parameter
            </Box>
            <Box sx={{ flex: "0 0 25%", fontSize: "0.875rem", fontWeight: 600, color: "primary.main" }}>
              Value
            </Box>
          </Box>

          <SectionBlock isFirst verticalLabel="Indoor Unit Side">
            <ParamRow
              label="Indoor Room Air Temperature"
              value={d.Tr_i}
              unit="°C"
            />
            <ParamRow
              label="Evaporator Pipe Temperature"
              value={d.Te_i}
              unit="°C"
            />
            <ParamRow
              label="Set Temperature by User"
              value={d.Tu_i}
              unit="°C"
            />
            <ParamRow label="IDU Fan Speed , 0..6" value={d.Flvl_i} />
            <ParamRow label="RPM of IDU Fan Motor" value={d.Frpm_i} />
            <ParamRow
              label="Indoor PM2.5 Sensor Reading"
              value={d.PM25_i}
              unit="ug/m3"
            />
            <ParamRow label="Filter Status" value={d.Fsta_i} />
          </SectionBlock>

          <SectionBlock verticalLabel="Outdoor Unit Side">
            <ParamRow
              label="Suction Pipe Temperature"
              value={d.Tsu_o}
              unit="°C"
            />
            <ParamRow
              label="Outdoor Air Temperature"
              value={d.Ta_o}
              unit="°C"
            />
            <ParamRow
              label="Condenser Pipe Temperature"
              value={d.Tc_o}
              unit="°C"
            />
            <ParamRow
              label="Compressor Top Pipe Temperature"
              value={d.Td_o}
              unit="°C"
            />
            <ParamRow
              label="Outdoor Fan Speed in RPM"
              value={d.Frpm_o}
              unit="RPM"
            />
            <ParamRow
              label="DC Current of U,V,W Phase to compressor"
              value={d.Pcur_o}
              unit="Ampere"
            />
            <ParamRow
              label="DC Voltage of Power rail"
              value={d.DCv_o}
              unit="Volts"
            />
            <ParamRow
              label="Total Input AC Current Taken by Outdoor Unit"
              value={d.ACc_o}
              unit="Ampere"
            />
            <ParamRow
              label="AC Voltage available at Input Power Supply"
              value={d.ACv_o}
              unit="Volts"
            />
            <ParamRow
              label="Temperature of IPM (Driving Circuit of Compressor)"
              value={d.Tipm_o}
              unit="°C"
            />
          </SectionBlock>

          <SectionBlock verticalLabel="Compressor RPM">
            <ParamRow
              label="The target RPM of Compressor decided by Algorithm"
              value={d.Trpm_c}
            />
            <ParamRow
              label="The RPM of Compressor set as per current situation"
              value={d.Srpm_c}
            />
            <ParamRow
              label="Realtime RPM of Compressor on which it is Running"
              value={d.Rrpm_c}
              unit="RPM"
            />
          </SectionBlock>

          <SectionBlock verticalLabel="Abnormal Operation">
            <ParamRow
              label="Reason due to Which RPM of Comp. has been Limited"
              value={d.Lrpm_a}
              unit="Byte"
            />
            <ParamRow label="Fault types" value={d.Falt_a} />
            <ParamRow
              label="Temporary Protections due to which the Unit has stopped Working"
              value={d.Prot_a}
            />
            <ParamRow
              label="Total Power On Time in Seconds"
              value={d.POn_t}
            />
          </SectionBlock>
        </Box>
      </Box>
    </Fragment>
  );
};

export default DiagnosticData;
